/**
 * Admin-only "View as user" support.
 *
 * The admin's actual Firebase auth session is preserved (so reads/writes still
 * use admin permissions). We only override which uid the app uses to load
 * `/users/{uid}/...` profile state, so the rest of the UI behaves as if the
 * impersonated user were logged in.
 */

import firebase from "firebase";

const STORAGE_KEY = "sommai.impersonatedUser.v1";

// Uids permitted to "View as user". Must match the read allowlist on
// /users/$uid in the Firebase Realtime Database rules — otherwise the modal
// will let you pick someone but the per-user reads will return permission_denied.
const ADMIN_UIDS = ["qBpe48vmtgcMOgDOAdI96ilkhb52"];

export function isAdminUid(uid) {
  if (!uid) return false;
  return ADMIN_UIDS.includes(String(uid));
}

// Kept for backwards compatibility with components that only have an email on
// hand. New call sites should prefer `isAdminUid` since the backend gate is
// uid-based.
export function isAdminEmail() {
  return false;
}

export function getImpersonatedUser() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.uid) return null;
    return {
      uid: String(parsed.uid),
      email: parsed.email ? String(parsed.email) : null,
      displayName: parsed.displayName ? String(parsed.displayName) : null,
    };
  } catch (err) {
    return null;
  }
}

export function setImpersonatedUser(user) {
  if (!user || !user.uid) return;
  window.localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      uid: user.uid,
      email: user.email || null,
      displayName: user.displayName || null,
    }),
  );
}

export function clearImpersonatedUser() {
  window.localStorage.removeItem(STORAGE_KEY);
}

export function getEffectiveUid(realUid) {
  const impersonated = getImpersonatedUser();
  return impersonated ? impersonated.uid : realUid;
}

/**
 * Returns the uid we should use for *reading* per-user data:
 *   - the impersonated uid if "View as user" is active,
 *   - otherwise the currently signed-in admin's uid.
 *
 * Lazily resolved against `firebase.auth().currentUser` at call time so this
 * helper can be used from any component without prop-drilling. Writes should
 * continue to use `firebase.auth().currentUser.uid` directly so admin actions
 * never silently mutate a customer's account.
 */
export function getEffectiveAuthUid() {
  const impersonated = getImpersonatedUser();
  if (impersonated && impersonated.uid) return impersonated.uid;
  try {
    const u = firebase.auth && firebase.auth() && firebase.auth().currentUser;
    return u ? u.uid : null;
  } catch (err) {
    return null;
  }
}

export function isImpersonating() {
  return !!getImpersonatedUser();
}
